// License Detail Page — Full license lifecycle management
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { personaBase } from '@/lib/persona';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { useToast } from '@/components/ui/use-toast';
import { ExpansionPacks } from '@/components/license/ExpansionPacks';
import { AllocationCard } from '@/components/account/AllocationCard';
import { OverageEvidence } from '@/components/account/OverageEvidence';
import { ArrowLeft, Copy, RefreshCw, Ban, Play, Trash2, Download, AlertTriangle, FlaskConical, KeyRound, Check, ExternalLink, Sparkles } from 'lucide-react';
import { StatusBadge as TrustStatusBadge } from '@/components/shared/StatusBadge';
import {
  getSaasDetails, getContacts, getOpportunities, getOrgMetrics, adminResetInitiate, getSaasTrial,
  tierLabel, nudgeTypeLabel, type SaasLicenseDetailsRow, type CommercialContactRow,
  type TrustUpsellOpportunityRow, type TrustOrgMetricsRow, type SaasTrialLookup,
} from '@/lib/api/trust';
import { SaasTrialSection, SaasTrialChip } from '@/components/trial/SaasTrialSection';
import { useCanMutate, useCurrentUser } from '@/context/PersonaProvider';

// Shape returned by GET /api/v1/licenses/:id (see backend license.service.ts).
// NOTE: this page was originally written against a mock with fields the real
// backend never returns (seats_*, product_name, features, max_api_calls_per_day,
// churn/upsell scores). Those are kept optional here purely for defensiveness;
// the render guards every one of them so a missing field can never crash the page.
interface LicenseOrg {
  id: string;
  name: string;
}
interface LicenseDetail {
  id: string;
  license_id?: string;        // human-facing key, e.g. VW-XXXX-...
  license_key?: string;       // legacy mock field name
  organization_id: string;
  organization?: LicenseOrg | null;
  organization_name?: string; // legacy mock field name
  tier: string;
  status: string;
  vendor_limit?: number;
  expires_at: string | null;
  issued_at: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
  scope?: 'platform' | 'org' | string | null;
  billing_mode?: string | null;
  overage_policy?: string | null;
  included_org_seats?: number | null;
  parent_license_id?: string | null;
  platform_features?: Record<string, unknown> | null;
  // BP8 commercial fields (returned by GET /licenses/:id select '*').
  contract_id?: string | null;
  is_trial?: boolean;
  customer_type?: string | null;
  vendor_pool?: number | null;
  org_pool?: number | null;
  overage_allowance_pct?: number | null;
  // legacy/optional — guarded in render
  product_name?: string;
  seats_total?: number;
  seats_used?: number;
  features?: Record<string, boolean | number | string>;
  max_api_calls_per_day?: number;
  hardware_id?: string | null;
  grace_period_days?: number;
  churn_risk_score?: number | null;
  upsell_score?: number | null;
  // 18H §4.1 — license-class awareness (saas records carry no JWT/phone-home;
  // their commercial data lives on saas_license_details, joined separately).
  license_class?: 'install' | 'platform' | 'saas' | null;
  white_label?: boolean;
}

interface ValidationEvent {
  id: string;
  timestamp: string;
  ip_address: string;
  success: boolean;
  error_code: string | null;
  response_time_ms: number;
}

export function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Persona- & origin-aware navigation: keep the user in their workspace and
  // return them to where they drilled in from (account / console / list).
  const location = useLocation();
  const origin = (location.state as { from?: { type?: string; id?: string; name?: string } } | null)?.from;
  const base = personaBase(location.pathname);
  const backTo = origin?.type === 'account' && origin.id ? `${base}/accounts/${origin.id}`
    : origin?.type === 'console' ? `${base}/console`
    : origin?.type === 'fleet' ? `${base}/fleet`
    : `${base}/licenses`;
  const { toast } = useToast();
  const canMutate = useCanMutate();
  const [confirm, setConfirm] = useState<'delete' | 'reveal-token' | null>(null);
  // Suspend and Revoke are audit-logged and the server REQUIRES a reason (1-500): they open
  // a ReasonDialog, never fire straight from the button (DF-32).
  const [reasonAction, setReasonAction] = useState<'suspend' | 'revoke' | null>(null);
  const [deleting, setDeleting] = useState(false);
  // DELETE /licenses/:id is super_admin only on the server; offering it to every admin
  // just produced a button that could only fail (DF-33). Deleting also needs canMutate, so the
  // read-only Audit workspace never offers it. useCurrentUser() alone is null-safe outside a
  // PersonaProvider ("not a super admin"), but useCanMutate() above reads usePersona(), which
  // THROWS there: a test or story that renders this page must mount the provider or mock it.
  const isSuperAdmin = useCurrentUser()?.role === 'super_admin';
  const canDelete = canMutate && isSuperAdmin;
  const [license, setLicense] = useState<LicenseDetail | null>(null);
  const [validations, setValidations] = useState<ValidationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Deliberate reveal: the JWT is never fetched as part of the license record.
  // It only ever lands here after an explicit, confirmed, audit-logged POST.
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [editingSeats, setEditingSeats] = useState(false);
  const [newSeats, setNewSeats] = useState('');
  const [editingExpiry, setEditingExpiry] = useState(false);
  const [newExpiry, setNewExpiry] = useState('');

  // Trust SaaS panel (18H §4.1) — only fetched/rendered for license_class='saas'.
  const [saasDetails, setSaasDetails] = useState<SaasLicenseDetailsRow | null>(null);
  const [saasContacts, setSaasContacts] = useState<CommercialContactRow[]>([]);
  const [saasOpportunities, setSaasOpportunities] = useState<TrustUpsellOpportunityRow[]>([]);
  const [saasMetrics, setSaasMetrics] = useState<TrustOrgMetricsRow | null>(null);
  const [saasLoadError, setSaasLoadError] = useState<string | null>(null);
  const [saasLoading, setSaasLoading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);

  // D-53 / OF-168 — the platform-trial clock of this saas licence's org. Its own
  // state, fetched on its own: a failure here is a Retry in the trial card, never
  // the page-wide "partial state" error the contacts/opportunities fetch raises.
  const [saasTrial, setSaasTrial] = useState<SaasTrialLookup | null>(null);
  const [saasTrialLoading, setSaasTrialLoading] = useState(false);
  const [saasTrialError, setSaasTrialError] = useState<string | null>(null);
  // Which lookup is current. Navigating from one saas licence to another (the
  // command palette does this) must not leave the PREVIOUS licence's trial panel
  // mounted - its "Adjust trial" would PATCH the previous org's allocation - nor let
  // a slow answer for the previous licence overwrite the newer one.
  const trialReq = useRef(0);

  const loadSaasTrial = async (licenseUuid: string) => {
    const req = ++trialReq.current;
    setSaasTrialLoading(true);
    setSaasTrialError(null);
    try {
      const lookup = await getSaasTrial(licenseUuid);
      if (req === trialReq.current) setSaasTrial(lookup);
    } catch (err) {
      if (req === trialReq.current) {
        setSaasTrialError(err instanceof Error ? err.message : 'Failed to load the platform trial');
      }
    } finally {
      if (req === trialReq.current) setSaasTrialLoading(false);
    }
  };

  const loadSaasPanel = async (licenseUuid: string) => {
    loadSaasTrial(licenseUuid);
    setSaasLoading(true);
    setSaasLoadError(null);
    try {
      const [details, contacts, opportunities, metrics] = await Promise.all([
        getSaasDetails({ licenseId: licenseUuid }),
        getContacts({ licenseId: licenseUuid }),
        getOpportunities({ licenseId: licenseUuid }),
        getOrgMetrics({ licenseId: licenseUuid }),
      ]);
      setSaasDetails(details[0] || null);
      setSaasContacts(contacts || []);
      setSaasOpportunities(opportunities || []);
      setSaasMetrics(metrics[0] || null);
    } catch (err) {
      // Partial state — the core license record above still rendered fine.
      setSaasLoadError(err instanceof Error ? err.message : 'Failed to load trust SaaS data');
    } finally {
      setSaasLoading(false);
    }
  };

  useEffect(() => {
    // A different licence: drop everything the previous saas licence loaded, and
    // invalidate any lookup still in flight for it, BEFORE the new fetches start.
    // saasDetails matters most - it decides whether the trial controls are offered.
    trialReq.current++;
    setSaasTrial(null);
    setSaasTrialError(null);
    setSaasTrialLoading(false);
    setSaasDetails(null);
    setSaasContacts([]);
    setSaasOpportunities([]);
    setSaasMetrics(null);
    setSaasLoadError(null);

    const load = async () => {
      // Decouple the two fetches: the license record is the page's source of
      // truth, the validations list is a secondary resource. allSettled ensures
      // a failing/missing sub-resource never nukes the main record (or worse,
      // triggers a mock fallback). No mock fallback exists anymore — on a real
      // failure we render an honest error/empty state.
      const [licResult, valResult] = await Promise.allSettled([
        apiRequest<LicenseDetail>(`/licenses/${id}`),
        apiRequest<ValidationEvent[]>(`/licenses/${id}/validations?limit=10`),
      ]);

      if (licResult.status === 'fulfilled') {
        setLicense(licResult.value);
        setLoadError(null);
        if (licResult.value.license_class === 'saas') {
          loadSaasPanel(licResult.value.id);
        }
      } else {
        setLicense(null);
        setLoadError(licResult.reason?.message || 'Failed to load license');
      }

      setValidations(valResult.status === 'fulfilled' ? (valResult.value || []) : []);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the licence id changes; loadSaasPanel/loadSaasTrial are render-scoped closures over state setters
  }, [id]);

  const copyKey = () => {
    navigator.clipboard.writeText(license?.license_id || license?.license_key || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [actionError, setActionError] = useState<string | null>(null);

  const refreshLicense = async () => {
    try {
      const updated = await apiRequest<LicenseDetail>(`/licenses/${id}`);
      setLicense(updated);
      setActionError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to refresh license');
    }
  };

  // POST a lifecycle action, then re-read the licence. Throws the server's message when the
  // POST fails, so a dialog can show it in place; a failed re-read after a successful POST is
  // only a stale page, never a failed action.
  const callAction = async (action: string, payload?: Record<string, unknown>) => {
    setActionLoading(action);
    try {
      await apiRequest(`/licenses/${id}/${action}`, {
        method: 'POST',
        body: payload ? JSON.stringify(payload) : undefined,
      });
    } finally {
      setActionLoading(null);
    }
    toast({ title: `License ${action === 'revoke' ? 'revoked' : action === 'suspend' ? 'suspended' : action === 'reactivate' ? 'reactivated' : action === 'renew' ? 'renewed' : 'updated'}.` });
    try {
      // Reload from the server — no optimistic/mock state changes.
      setLicense(await apiRequest<LicenseDetail>(`/licenses/${id}`));
    } catch {
      toast({ title: 'Done, but this page could not refresh. Reload to see the new state.' });
    }
  };

  // The inline buttons (renew, seats, expiry, reactivate) report a failure in the page banner.
  const performAction = async (action: string, payload?: Record<string, unknown>) => {
    if (!license || !canMutate || actionLoading) return;
    setActionError(null);
    try {
      await callAction(action, payload);
    } catch (err) {
      // Honest failure: surface the error, leave server-truth state intact.
      setActionError(err instanceof Error ? err.message : `Action "${action}" failed`);
    }
  };

  // Suspend / Revoke: a failure is thrown to the ReasonDialog, which stays open and shows it.
  const submitReason = async (reason: string) => {
    if (!license || !reasonAction || !canMutate) return;
    setActionError(null);
    await callAction(reasonAction, { reason });
    setReasonAction(null);
  };

  const updateSeats = async () => {
    const seats = parseInt(newSeats);
    if (isNaN(seats) || seats < 1) return;
    await performAction('update-seats', { seats_total: seats });
    setEditingSeats(false);
  };

  const updateExpiry = async () => {
    if (!newExpiry) return;
    await performAction('update-expiry', { expires_at: new Date(newExpiry).toISOString() });
    setEditingExpiry(false);
  };

  // A failed delete says so and stays put: it used to swallow the error and navigate away,
  // which read as success while the licence was still there (DF-33).
  const deleteLicense = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    setActionError(null);
    try {
      await apiRequest(`/licenses/${id}`, { method: 'DELETE' });
      toast({ title: 'License deleted.' });
      navigate(`${base}/licenses`);
    } catch (err) {
      setConfirm(null);
      setActionError(err instanceof Error ? err.message : 'Failed to delete the license');
    } finally {
      setDeleting(false);
    }
  };

  const revealToken = async () => {
    setRevealLoading(true);
    setActionError(null);
    try {
      const resp = await apiRequest<{ license_id: string; jwt_token: string }>(`/licenses/${id}/token`, {
        method: 'POST',
      });
      setRevealedToken(resp.jwt_token);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to reveal license token', variant: 'destructive' as any });
    } finally {
      setRevealLoading(false);
    }
  };

  // D-44 — admin-reset-initiation: operator-triggered Supabase recovery email
  // for this saas org's admin contact. AAL2-gated server-side; here it's just
  // a guardrail confirm modal, never a credential in this app's hands.
  const doAdminReset = async () => {
    if (!license) return;
    setResetBusy(true);
    setResetResult(null);
    try {
      const result = await adminResetInitiate(license.id);
      setResetResult(
        result.status === 'sent' ? 'Recovery email sent.'
          : result.status === 'email_mismatch_review' ? 'Contact email mismatch — resolve in contact reconciliation first.'
          : result.status === 'rate_limited' ? 'Rate limited — try again shortly.'
          : 'No matching admin account found.'
      );
      toast({ title: result.status === 'sent' ? 'Recovery email sent.' : `Reset not sent: ${result.status.replace(/_/g, ' ')}` });
    } catch (err) {
      setResetResult(err instanceof Error ? err.message : 'Failed to initiate reset');
    } finally {
      setResetBusy(false);
    }
  };

  const copyRevealedToken = () => {
    if (!revealedToken) return;
    navigator.clipboard.writeText(revealedToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const downloadRevealedToken = () => {
    if (!revealedToken || !license) return;
    const filename = `${license.license_id || license.license_key || license.id}.license`;
    const blob = new Blob([revealedToken], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleConfirm = () => {
    // Delete keeps its dialog open (busy) until the request settles; reveal closes at once.
    if (confirm === 'delete') deleteLicense();
    else if (confirm === 'reveal-token') {
      setConfirm(null);
      revealToken();
    }
  };

  // Convert-to-paid (BP8 verb): a trial becomes a paying customer by issuing a
  // real license for the same org. KISS — reuse the Issue License flow, pre-filled
  // with this trial's org + pool as a paid (professional) license. No separate
  // backend endpoint; the operator confirms terms in the standard issue form.
  const convertToPaid = () => {
    if (!license) return;
    const params = new URLSearchParams();
    if (license.organization_id) params.set('org', license.organization_id);
    if (license.vendor_pool) params.set('seats', String(license.vendor_pool));
    params.set('convertedFrom', license.license_id || license.id);
    navigate(`${base}/licenses/new?${params.toString()}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin" style={{ width: '32px', height: '32px', border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading license...</p>
        </div>
      </div>
    );
  }

  if (!license) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Link to={`${base}/licenses`} style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
            <ArrowLeft size={18} />
          </Link>
          <h1 className="page-title">License</h1>
        </div>
        <div style={{ padding: '24px', background: 'var(--ps-pill-red-bg)', border: '1px solid var(--ps-pill-red-bg)', borderRadius: '8px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={18} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>Unable to load this license.</div>
            <div style={{ fontSize: '13px', marginTop: '4px', color: 'var(--ps-pill-red-tx)' }}>{loadError || 'License not found.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilExpiry = license.expires_at
    ? Math.ceil((new Date(license.expires_at).getTime() - Date.now()) / 86400000)
    : null;

  // D-11: derive an EFFECTIVE display status. A stored status of 'active' is
  // misleading once the license is past its expiry; show "Expired" instead of a
  // green Active badge over an expired body. Revoked/suspended always win.
  const isPastExpiry = license.expires_at
    ? new Date(license.expires_at).getTime() < Date.now()
    : false;
  const effectiveStatus =
    license.status === 'active' && isPastExpiry ? 'expired' : license.status;

  // D-9: license scope (Platform / Org) badge.
  const scope = (license.scope || 'org').toString().toLowerCase();
  const scopeLabel = scope === 'platform' ? 'Platform' : 'Org';
  const scopeStyle: React.CSSProperties = scope === 'platform'
    ? { background: 'var(--ps-pill-blue-bg)', color: 'var(--ps-pill-blue-tx)', border: '1px solid var(--ps-pill-blue-bg)' }
    : { background: 'var(--ps-pill-gray-bg)', color: 'var(--ps-pill-gray-tx)', border: '1px solid var(--ps-pill-gray-bg)' };

  // Display values reconciled to the real backend record (with mock-era fallbacks).
  const displayKey = license.license_id || license.license_key || license.id;
  const orgName = license.organization?.name || license.organization_name || '—';
  const hasSeats = typeof license.seats_total === 'number' && license.seats_total > 0;
  const seatsTotal = license.seats_total ?? 0;
  const seatsUsed = license.seats_used ?? 0;
  const features = license.features || {};
  // D-34 — a saas-class record is commercial-only: no JWT, no phone-home, no
  // /validate involvement ever. The absence of those panels below IS the
  // design, not an omission.
  const isSaas = license.license_class === 'saas';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to={backTo} style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} aria-label="Back">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="page-title" style={{ fontFamily: 'monospace', fontSize: '20px' }}>{displayKey}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              {isSaas ? <TrustStatusBadge status="saas" /> : <StatusBadge status={effectiveStatus} />}
              {isSaas && <SaasTrialChip allocation={saasTrial?.allocation} />}
              <span style={{ ...scopeStyle, display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                {scopeLabel}
              </span>
              {license.white_label && (
                <span style={{ background: 'var(--ps-pill-amber-bg)', color: 'var(--ps-pill-amber-tx)', border: '1px solid var(--ps-pill-amber-bg)', display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  White label
                </span>
              )}
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                <Link to={`${base}/accounts/${license.organization_id}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                  {orgName}
                </Link>
                {!isSaas && <>{' · '}{license.tier}</>}
                {isSaas && saasDetails && <>{' · '}{tierLabel(saasDetails.tier)}</>}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={copyKey} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            <Copy size={14} /> {copied ? 'Copied!' : 'Copy Key'}
          </button>
          {/* D-34 — saas records have no JWT; reveal/suspend/reactivate/renew/revoke
              are install phone-home lifecycle actions that don't apply here. This
              absence is the design, not an omission. */}
          {canMutate && !isSaas && (
            <button onClick={() => setConfirm('reveal-token')} disabled={revealLoading} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: revealLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              <KeyRound size={14} /> {revealLoading ? 'Revealing...' : 'Reveal license token'}
            </button>
          )}
          {canMutate && isSaas && (
            <button onClick={() => setResetConfirm(true)} disabled={resetBusy} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: resetBusy ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              <KeyRound size={14} /> {resetBusy ? 'Sending…' : 'Send admin reset'}
            </button>
          )}
          {canMutate && !isSaas && (license.is_trial || license.tier === 'trial') && license.status !== 'revoked' && (
            <button onClick={convertToPaid} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #16A34A', background: '#16A34A', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FlaskConical size={14} /> Convert to Paid
            </button>
          )}
          {canMutate && !isSaas && license.status === 'active' && (
            <button onClick={() => setReasonAction('suspend')} disabled={actionLoading === 'suspend'} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--ps-pill-amber-bg)', background: 'var(--ps-pill-amber-bg)', color: 'var(--ps-pill-amber-tx)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Ban size={14} /> {actionLoading === 'suspend' ? 'Suspending...' : 'Suspend'}
            </button>
          )}
          {canMutate && !isSaas && license.status === 'suspended' && (
            <button onClick={() => performAction('reactivate')} disabled={actionLoading === 'reactivate'} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--ps-pill-green-bg)', background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Play size={14} /> {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate'}
            </button>
          )}
          {canMutate && !isSaas && (
            <button onClick={() => performAction('renew')} disabled={actionLoading === 'renew'} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RefreshCw size={14} /> {actionLoading === 'renew' ? 'Renewing...' : 'Renew +1yr'}
            </button>
          )}
          {canMutate && !isSaas && license.status !== 'revoked' && (
            <button onClick={() => setReasonAction('revoke')} disabled={actionLoading === 'revoke'} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Ban size={14} /> {actionLoading === 'revoke' ? 'Revoking...' : 'Revoke'}
            </button>
          )}
          {canDelete && (
            <button onClick={() => setConfirm('delete')} disabled={actionLoading !== null || deleting} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <Breadcrumb crumbs={
        origin?.type === 'account' && origin.id
          ? [{ label: 'Accounts', to: `${base}/accounts` }, { label: origin.name || orgName, to: `${base}/accounts/${origin.id}` }, { label: displayKey }]
          : origin?.type === 'console'
            ? [{ label: 'Account Console', to: `${base}/console` }, { label: displayKey }]
            : origin?.type === 'fleet'
              ? [{ label: 'Fleet', to: `${base}/fleet` }, { label: origin.name || displayKey }]
              : [{ label: 'Licenses', to: `${base}/licenses` }, { label: displayKey }]
      } />

      {/* Expiry warning */}
      {!isSaas && daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0 && (
        <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} />
          This license expires in {daysUntilExpiry} days.
          {canMutate && <button onClick={() => performAction('renew')} style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', border: 'none', background: '#92400E', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Renew Now</button>}
        </div>
      )}
      {!isSaas && daysUntilExpiry !== null && daysUntilExpiry <= 0 && license.status !== 'revoked' && (
        <div style={{ padding: '10px 12px', background: 'var(--ps-pill-red-bg)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} /> This license has expired.
        </div>
      )}
      {isSaas && (
        <div style={{ padding: '10px 12px', background: 'var(--accent-light)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={14} color="var(--accent)" />
          This is a <strong>saas-class</strong> commercial record — no JWT, no phone-home, no <code>/validate</code> involvement, ever. Entitlements are enforced entirely on the TPRM7 side.
        </div>
      )}
      {resetResult && (
        <div style={{ padding: '10px 12px', background: 'var(--ps-pill-gray-bg)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {resetResult}
        </div>
      )}
      {actionError && (
        <div role="alert" style={{ padding: '10px 12px', background: 'var(--ps-pill-red-bg)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} /> {actionError}
        </div>
      )}

      {/* Revealed license token — never fetched as part of the license record.
          Only ever appears here after an explicit, confirmed, audit-logged reveal. */}
      {revealedToken && (
        <div className="card" style={{ border: '1px solid var(--ps-pill-amber-bg)', background: 'var(--ps-pill-amber-bg)', marginBottom: '16px' }}>
          <div className="card-header" style={{ background: 'transparent' }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ps-pill-amber-tx)' }}>
              <KeyRound size={16} /> License token revealed
            </span>
            <button onClick={() => setRevealedToken(null)} style={{ fontSize: '12px', color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>
              Hide
            </button>
          </div>
          <div className="card-body">
            <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <AlertTriangle size={14} style={{ marginTop: '1px', flexShrink: 0 }} />
              <span>This token is the <strong>install credential</strong> for this license. This reveal was audit-logged. Copy or download it now — treat it as a secret.</span>
            </div>
            {/* ps-no-capture: rrweb blockClass. This is the signed install
                credential. Fenced on the textarea alone so the surrounding
                warning and controls still replay. */}
            <textarea
              className="ps-no-capture"
              readOnly
              value={revealedToken}
              onFocus={e => e.target.select()}
              rows={6}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                fontSize: '12px',
                fontFamily: 'monospace',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'var(--surface)',
                overflowY: 'auto',
                marginBottom: '12px',
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={copyRevealedToken} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {tokenCopied ? <Check size={14} /> : <Copy size={14} />} {tokenCopied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={downloadRevealedToken} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <Download size={14} /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Core Details */}
        <div className="card">
          <div className="card-header"><span className="card-title">License Details</span></div>
          <div className="card-body">
            <table style={{ width: '100%' }}>
              <tbody>
                {([
                  ['License Key', <code style={{ fontSize: '12px', background: 'var(--bg-muted)', padding: '2px 6px', borderRadius: '4px' }}>{displayKey}</code>],
                  ['Organization', <Link to={`${base}/accounts/${license.organization_id}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{orgName}</Link>],
                  isSaas
                    ? ['Trust Tier', saasDetails ? tierLabel(saasDetails.tier) : (saasLoading ? 'Loading…' : '—')]
                    : ['Tier', <span style={{ textTransform: 'capitalize' }}>{license.tier}</span>],
                  isSaas
                    ? ['Status', saasDetails ? <TrustStatusBadge status={saasDetails.status} /> : <StatusBadge status={effectiveStatus} />]
                    : ['Status', <StatusBadge status={effectiveStatus} />],
                  ['Scope', <span style={{ ...scopeStyle, display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{scopeLabel}</span>],
                  !isSaas && typeof license.vendor_limit === 'number' ? [scope === 'platform' ? 'Vendor pool (platform total)' : 'Vendor Limit', license.vendor_limit.toLocaleString()] : null,
                  !isSaas && license.billing_mode ? ['Billing Mode', <span style={{ textTransform: 'capitalize' }}>{license.billing_mode}</span>] : null,
                  !isSaas && license.overage_policy ? ['Overage Policy', <span style={{ textTransform: 'capitalize' }}>{license.overage_policy}</span>] : null,
                  isSaas && saasDetails?.tprm7_org_id ? ['TPRM7 org binding', <code style={{ fontSize: '11.5px' }}>{saasDetails.tprm7_org_id}</code>] : null,
                  isSaas && saasDetails ? ['Acquisition source', <span style={{ textTransform: 'capitalize' }}>{(saasDetails.acquisition_source || '—').replace(/_/g, ' ')}</span>] : null,
                  isSaas && saasDetails?.stripe_customer_id
                    ? ['Stripe customer', <a href={`https://dashboard.stripe.com/customers/${saasDetails.stripe_customer_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{saasDetails.stripe_customer_id}<ExternalLink size={11} /></a>]
                    : isSaas ? ['Stripe customer', <span style={{ color: 'var(--text-muted)' }}>No conversion yet</span>] : null,
                  !isSaas ? ['Issued', new Date(license.issued_at).toLocaleString()] : null,
                  ['Updated', formatRelativeTime(license.updated_at)],
                  license.product_name ? ['Product', license.product_name] : null,
                  !isSaas && license.hardware_id ? ['Hardware Lock', license.hardware_id] : null,
                  !isSaas && typeof license.grace_period_days === 'number' ? ['Grace Period', `${license.grace_period_days} days`] : null,
                  !isSaas && typeof license.max_api_calls_per_day === 'number' ? ['API Calls/Day', license.max_api_calls_per_day.toLocaleString()] : null,
                ].filter(Boolean) as [string, React.ReactNode][]).map(([key, val]) => (
                  <tr key={String(key)}>
                    <td style={{ padding: '8px 0', fontSize: '12px', color: 'var(--text-muted)', width: '40%', verticalAlign: 'top' }}>{key}</td>
                    <td style={{ padding: '8px 0', fontSize: '13px', fontWeight: 500 }}>{val as React.ReactNode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Seats & Expiry */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {hasSeats && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Seats</span>
              {canMutate && <button onClick={() => { setEditingSeats(!editingSeats); setNewSeats(String(seatsTotal)); }} style={{ fontSize: '12px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
                {editingSeats ? 'Cancel' : 'Edit'}
              </button>}
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent)' }}>{seatsUsed}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Used</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ height: '8px', background: 'var(--bg-muted)', borderRadius: '4px', marginBottom: '4px' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (seatsUsed / seatsTotal) * 100)}%`, background: seatsUsed >= seatsTotal ? 'var(--status-expired)' : 'var(--accent)', borderRadius: '4px', transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{seatsUsed}/{seatsTotal} seats used ({Math.round((seatsUsed / seatsTotal) * 100)}%)</div>
                </div>
              </div>
              {canMutate && editingSeats && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input type="number" value={newSeats} onChange={e => setNewSeats(e.target.value)} min="1" style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }} />
                  <button onClick={updateSeats} style={{ padding: '6px 12px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Save</button>
                </div>
              )}
            </div>
          </div>
          )}

          {!isSaas && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Expiry</span>
              {canMutate && <button onClick={() => { setEditingExpiry(!editingExpiry); setNewExpiry(license.expires_at ? new Date(license.expires_at).toISOString().split('T')[0] : ''); }} style={{ fontSize: '12px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
                {editingExpiry ? 'Cancel' : 'Edit'}
              </button>}
            </div>
            <div className="card-body">
              <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px', color: daysUntilExpiry !== null && daysUntilExpiry <= 30 ? 'var(--status-expired)' : 'var(--text-primary)' }}>
                {license.expires_at ? new Date(license.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Never'}
              </div>
              {daysUntilExpiry !== null && (
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {daysUntilExpiry > 0 ? `${daysUntilExpiry} days remaining` : `Expired ${Math.abs(daysUntilExpiry)} days ago`}
                </div>
              )}
              {canMutate && editingExpiry && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }} />
                  <button onClick={updateExpiry} style={{ padding: '6px 12px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Save</button>
                </div>
              )}
            </div>
          </div>
          )}

          {/* D-34 — display-only entitlement/storage snapshot for a saas org.
              No checkout lives here (that's the customer portal's job); this is
              read-only telemetry + the "Manage billing →" idea expressed as data. */}
          {isSaas && (
            <div className="card">
              <div className="card-header"><span className="card-title">Trust org snapshot</span></div>
              <div className="card-body">
                {saasLoading ? (
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</div>
                ) : saasLoadError ? (
                  <div style={{ fontSize: '13px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={14} /> {saasLoadError}
                  </div>
                ) : saasMetrics ? (
                  <table style={{ width: '100%' }}>
                    <tbody>
                      {([
                        ['Published', <TrustStatusBadge status={saasMetrics.metrics.published ? 'active' : 'suspended'} />],
                        ['Teaser / Ring-1 views', `${saasMetrics.metrics.teaser_views} / ${saasMetrics.metrics.ring1_views}`],
                        ['Requests / Grants', `${saasMetrics.metrics.requests_received} / ${saasMetrics.metrics.grants_issued}`],
                        ['Answers stale', `${saasMetrics.metrics.answers_stale}/${saasMetrics.metrics.answers_total}`],
                        ['Single admin risk', saasMetrics.metrics.single_admin ? 'Yes' : 'No'],
                        ['Verified domains', `${saasMetrics.metrics.verified_domains} (${saasMetrics.metrics.domains_in_grace} in grace)`],
                        ['Last ingest', formatRelativeTime(saasMetrics.ingested_at)],
                      ] as [string, React.ReactNode][]).map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ padding: '6px 0', fontSize: '12px', color: 'var(--text-muted)', width: '55%' }}>{k}</td>
                          <td style={{ padding: '6px 0', fontSize: '13px', fontWeight: 500 }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No telemetry has landed yet for this org.</div>
                )}
              </div>
            </div>
          )}

          {/* Scores */}
          {!isSaas && (license.churn_risk_score != null || license.upsell_score != null) && (
            <div className="card">
              <div className="card-header"><span className="card-title">ML Scores</span></div>
              <div className="card-body">
                {license.churn_risk_score != null && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Churn Risk</span>
                      <span style={{ fontWeight: 600, color: license.churn_risk_score > 0.6 ? '#DC2626' : license.churn_risk_score > 0.3 ? '#D97706' : '#16A34A' }}>
                        {(license.churn_risk_score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--bg-muted)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${license.churn_risk_score * 100}%`, background: license.churn_risk_score > 0.6 ? '#DC2626' : license.churn_risk_score > 0.3 ? '#D97706' : '#16A34A', borderRadius: '3px' }} />
                    </div>
                  </div>
                )}
                {license.upsell_score != null && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Upsell Potential</span>
                      <span style={{ fontWeight: 600, color: license.upsell_score > 0.6 ? '#16A34A' : 'var(--text-secondary)' }}>
                        {(license.upsell_score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--bg-muted)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${license.upsell_score * 100}%`, background: '#4F46E5', borderRadius: '3px' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      {Object.keys(features).length > 0 && (
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-header"><span className="card-title">Features</span></div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
              {Object.entries(features).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: value === true ? '#16A34A' : value === false ? '#DC2626' : 'var(--text-primary)' }}>
                    {typeof value === 'boolean' ? (value ? '✓' : '✗') : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trial banner (BP8) — status + convert affordance */}
      {!isSaas && (license.is_trial || license.tier === 'trial') && (
        <div style={{ padding: '10px 14px', background: 'var(--ps-pill-purple-bg)', borderRadius: '8px', marginTop: '16px', fontSize: '13px', color: 'var(--ps-pill-purple-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FlaskConical size={14} />
          This is a <strong>trial</strong>{daysUntilExpiry != null && daysUntilExpiry > 0 ? ` — ${daysUntilExpiry} days left` : ''}.
          {canMutate && license.status !== 'revoked' && (
            <button onClick={convertToPaid} style={{ marginLeft: '8px', padding: '3px 10px', borderRadius: '6px', border: 'none', background: '#7C3AED', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              Convert to Paid →
            </button>
          )}
        </div>
      )}

      {/* D-34 — install/platform-only panels (pools, expansion packs, overage
          evidence, /validate history). Absent for saas by design. */}
      {!isSaas && (
        <>
          <AllocationCard
            licenseId={id!}
            vendorPool={license.vendor_pool ?? null}
            orgPool={license.org_pool ?? null}
            overageAllowancePct={license.overage_allowance_pct ?? 0}
            readOnly={!canMutate}
            onSaved={() => { void refreshLicense(); }}
          />
          <ExpansionPacks licenseId={id!} readOnly={!canMutate} />
          <OverageEvidence licenseId={id!} licenseKey={license.license_id || license.license_key} />

          <div className="card" style={{ marginTop: '16px' }}>
            <div className="card-header">
              <span className="card-title">Recent Validations</span>
              <Link to={`${base}/licenses/${id}/validations`} style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>View all</Link>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Time', 'IP Address', 'Status', 'Response Time'].map(h => (
                      <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validations.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: '20px 16px', fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>
                        No validations recorded yet.
                      </td>
                    </tr>
                  )}
                  {validations.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>{formatRelativeTime(v.timestamp)}</td>
                      <td style={{ padding: '8px 16px', fontSize: '12px', fontFamily: 'monospace' }}>{v.ip_address}</td>
                      <td style={{ padding: '8px 16px' }}>
                        {v.success
                          ? <span style={{ fontSize: '12px', color: '#16A34A', fontWeight: 600 }}>✓ Valid</span>
                          : <span style={{ fontSize: '12px', color: '#DC2626', fontWeight: 600 }}>✗ {v.error_code}</span>
                        }
                      </td>
                      <td style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>{v.response_time_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* D-53 / OF-168 — the platform trial of this free org: the same per-company
          clock Sales › Account edits, reached through the org's allocation. The
          install-class trial banner above is a different thing (a trial LICENCE). */}
      {isSaas && (
        <SaasTrialSection
          key={license.id}
          tier={saasDetails?.tier ?? null}
          status={saasDetails?.status ?? null}
          lookup={saasTrial}
          loading={saasTrialLoading}
          error={saasTrialError}
          onRetry={() => loadSaasTrial(license.id)}
          onChanged={() => loadSaasTrial(license.id)}
        />
      )}

      {/* D-34/D-33 — the saas equivalent: commercial contacts + opportunity
          history in place of pools/validations. */}
      {isSaas && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Commercial contacts</span></div>
            <div className="card-body" style={{ padding: 0 }}>
              {saasLoading ? (
                <div style={{ padding: '20px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</div>
              ) : saasContacts.length === 0 ? (
                <div style={{ padding: '20px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>No contacts on file yet.</div>
              ) : (
                <div>
                  {saasContacts.map((c) => (
                    <div key={c.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      {/* ps-mask: rrweb maskTextClass (A20). The SaaS contacts
                          synced off the customer's own tenant — named people
                          who never saw this console. The role on the right is
                          not masked: it is a category, not an identity, and it
                          is half of what a sync bug looks like. */}
                      <div className="ps-mask">
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.name || c.email}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{c.email}</div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{c.role.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Opportunity history</span>
              <Link to={`${base}/trust/opportunities`} style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>View all</Link>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {saasLoading ? (
                <div style={{ padding: '20px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</div>
              ) : saasOpportunities.length === 0 ? (
                <div style={{ padding: '20px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>No upsell opportunities opened yet.</div>
              ) : (
                <div>
                  {saasOpportunities.map((o) => (
                    <div key={o.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>{nudgeTypeLabel(o.trigger_type)}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{formatRelativeTime(o.opened_at)}</div>
                      </div>
                      <TrustStatusBadge status={o.state} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Metadata */}
      {Object.keys(license.metadata).length > 0 && (
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-header"><span className="card-title">Metadata</span></div>
          <div className="card-body">
            <pre style={{ fontSize: '12px', fontFamily: 'monospace', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '6px', overflowX: 'auto', margin: 0 }}>
              {JSON.stringify(license.metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <ReasonDialog
        open={reasonAction === 'suspend'}
        title="Suspend license?"
        body="Installs using this key stop validating until you reactivate it. The reason is logged to the audit trail."
        confirmLabel="Suspend"
        maxLength={500}
        onConfirm={submitReason}
        onCancel={() => setReasonAction(null)}
      />

      {/* Revoke is irreversible and wide-reaching: a reason AND a typed confirm (36E §1.2). The
          phrase is the last four characters of this key, so it cannot be satisfied by habit. */}
      <ReasonDialog
        open={reasonAction === 'revoke'}
        title="Revoke license?"
        body="Installs using this key stop validating and its seats free back to the pool. This cannot be undone. The reason is logged to the audit trail."
        confirmLabel="Revoke license"
        danger
        maxLength={500}
        confirmPhrase={displayKey.slice(-4)}
        onConfirm={submitReason}
        onCancel={() => setReasonAction(null)}
      />

      <ConfirmDialog
        open={confirm !== null}
        title={confirm === 'delete' ? 'Delete license?' : 'Reveal license token?'}
        body={
          confirm === 'delete'
            ? 'Permanently delete this license? This cannot be undone.'
            : 'This will reveal the signed license token — the install credential for this license. The reveal is audit-logged. Only proceed if you need to hand this credential to an install.'
        }
        confirmLabel={confirm === 'delete' ? 'Delete' : 'Reveal'}
        danger={confirm === 'delete'}
        busy={deleting}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      {/* D-44 — admin-reset-initiation guardrail. This app never sees or sets
          a credential; it only triggers a Supabase recovery email. */}
      <ConfirmDialog
        open={resetConfirm}
        title="Send admin reset?"
        body="This sends a Supabase recovery email to this org's admin contact. You will not see or set any credential."
        confirmLabel="Send"
        onConfirm={() => { setResetConfirm(false); doAdminReset(); }}
        onCancel={() => setResetConfirm(false)}
      />
    </div>
  );
}
